<template lang="pug">
  .gl-settings
    // nav
    gl-nav(
      @submit-changes="upsertConfig"
      @cancel-changes="cancelChanges"
      :options="{ unsavedChanges: pendingForm.length, isLoading: isLoading.upsert }")

    // sidenav
    gl-settings-sidenav

    // page
    .gl-settings__page.gl-layout--medium-width(layout="row")

      // content
      .gl-settings__page__content(flex)

        // head
        .gl-settings__head
          .gl-settings__head__context App Settings
          .gl-settings__head__title Appearance

        // favicon and logos
        .gl-layout__segment(layout="column" layout-gt-md="row")
          .gl-layout__segment__meta
            .gl-layout__segment__title Favicon and Logos
            .gl-layout__segment__description Ensure your branding is on point both in the browser and in all emails sent from the app.
          .gl-layout__segment__content(flex)

            // favicon
            b-form.gl-card.gl-card--info(@submit.prevent="submitFavicon" novalidate)
              gl-alert(
                :options="{ errorList: errorList.favicon, successList: successList.favicon }"
                @clear-error-list="errorList.favicon = []"
                @clear-success-list="successList.favicon = []")
              div(layout="row" layout-align="space-between center")
                .gl-card--info__image(v-if="modelConfig.agency.favicon")
                  img(:src="modelConfig.agency.favicon")
                div(flex)
                  .gl-card--info__title Favicon
                  .gl-card--info__description
                    span.pr-4 Upload a browser favicon. For best results upload, 16x16 or 32x32 (px)
                    b.pr-4 .png
                    span image file.
                .gl-card--info__btn
                  gl-img-upload(@base64="(base64) => setConfigAttr({ attr: 'favicon', value: base64 })")

            // headerLogo
            b-form.gl-card.gl-card--info(@submit.prevent="submitFavicon" novalidate)
              gl-alert(
                :options="{ errorList: errorList.headerLogo, successList: successList.headerLogo }"
                @clear-error-list="errorList.headerLogo = []"
                @clear-success-list="successList.headerLogo = []")
              div(layout="row" layout-align="space-between center")
                .gl-card--info__image(:style="{ backgroundColor: modelConfig.agency.headerBgColor }" v-if="modelConfig.agency.headerLogo")
                  img(:src="modelConfig.agency.headerLogo")
                div(flex)
                  .gl-card--info__title Top Bar Logo
                  .gl-card--info__description
                    span.pr-4 Upload a logo to display in the header bar at the top of the page. A
                    b.pr-4 .png
                    span image file with a transparent background is recommended.
                .gl-card--info__btn
                  gl-img-upload(@base64="(base64) => setConfigAttr({ attr: 'headerLogo', value: base64 })")

            // loginLogo
            b-form.gl-card.gl-card--info(@submit.prevent="submitFavicon" novalidate)
              gl-alert(
                :options="{ errorList: errorList.loginLogo, successList: successList.loginLogo }"
                @clear-error-list="errorList.loginLogo = []"
                @clear-success-list="successList.loginLogo = []")
              div(layout="row" layout-align="space-between center")
                .gl-card--info__image(v-if="modelConfig.agency.loginLogo")
                  img(:src="modelConfig.agency.loginLogo")
                div(flex)
                  .gl-card--info__title Login Logo
                  .gl-card--info__description
                    span.pr-4 Upload a logo to display in the middle of all unauthenticated pages, eg: Login page. A
                    b.pr-4 .png
                    span image file with a transparent background is recommended.
                .gl-card--info__btn(layout="row" layout-align="end center")
                  gl-img-upload(@base64="(base64) => setConfigAttr({ attr: 'loginLogo', value: base64 }) ")

            // emailLogo
            b-form.gl-card.gl-card--info(@submit.prevent="submitFavicon" novalidate)
              gl-alert(
                :options="{ errorList: errorList.emailLogo, successList: successList.emailLogo }"
                @clear-error-list="errorList.emailLogo = []"
                @clear-success-list="successList.emailLogo = []")
              div(layout="row" layout-align="space-between center")
                .gl-card--info__image(v-if="modelConfig.agency.loginLogo")
                  img(:src="modelConfig.agency.emailLogo")
                div(flex)
                  .gl-card--info__title Email Template Logo
                  .gl-card--info__description
                    span.pr-4 Upload a logo to display at the top of all app emails. A
                    b.pr-4 .png
                    span.pr-4 or
                    b.pr-4 .jpg
                    span image file with a transparent or white background is recommended.
                .gl-card--info__btn
                  gl-img-upload(@base64="(base64) => setConfigAttr({ attr: 'emailLogo', value: base64 }) ")
        .gl-layout__divider

        // theme options
        .gl-layout__segment(layout="column" layout-gt-md="row")
          .gl-layout__segment__meta
            .gl-layout__segment__title Theme Settings
            .gl-layout__segment__description Customize the font, colors, and styles used throughout the app experience to match your branding.
          .gl-layout__segment__content(flex)
            b-form.gl-card.gl-card--info.pt-0(novalidate)

              // alerts
              gl-alert(
                :options="{ errorList: errorList.favicon, successList: successList.favicon }"
                @clear-error-list="errorList.favicon = []"
                @clear-success-list="successList.favicon = []")

              // General Settings
              .gl-card--info__heading.no-top General Settings

              // bgColor
              .gl-card--info__meta
                .gl-card--info__title Background Color
                .gl-card--info__description Pick a body background color. Primarily seen on the login screen.
              gl-color-picker(:hex="modelConfig.agency.bgColor" @hex-updated="(hex) => setConfigAttr({ attr: 'bgColor', value: hex })")

              // fontFamily
              .gl-card--info__meta
                .gl-card--info__title Font Family
                .gl-card--info__description Select the base font family to use throughout the application.
              b-form-select(:options="fontFamilies" v-model="modelConfig.agency.fontFamily" @change="(value) => setConfigAttr({ attr: 'fontFamily', value: value })")

              // textColor
              //- .gl-card--info__meta
              //-   .gl-card--info__title Text Color
              //-   .gl-card--info__description Lorem ipsum dolor sit amet, consectetur adipiscing elit
              //- gl-color-picker(:hex="modelConfig.agency.textColor" @hex-updated="(hex) => setConfigAttr({ attr: 'textColor', value: hex })")

              // linkColor
              .gl-card--info__meta
                .gl-card--info__title Link Color
                .gl-card--info__description Pick the link text color. Used for the basic links included in the text blocks.
              gl-color-picker(:hex="modelConfig.agency.linkColor" @hex-updated="(hex) => setConfigAttr({ attr: 'linkColor', value: hex })")

              //- // dashboard page title
              //- b-form-group(label="Dashboard Page Title" label-for="d-title" flex)
              //-   b-form-input(
              //-     @input="(input) => setConfigAttr({ attr: 'dashboardPageTitle', value: input })"
              //-     v-model.trim="modelConfig.agency.dashboardPageTitle"
              //-     id="d-title"
              //-     required
              //-     type="text")
              //-   small By default, this is "Dashboard".

              // Form
              .gl-card--info__heading Form Settings

              // brandBtnBgColor
              .gl-card--info__meta
                .gl-card--info__title Primary Button Background Color
                .gl-card--info__description Background color for buttons in forms eg: Below inputs on login page.
              gl-color-picker(:hex="modelConfig.agency.brandBtnBgColor" @hex-updated="(hex) => setConfigAttr({ attr: 'brandBtnBgColor', value: hex })")

              // brandBtnTextColor
              .gl-card--info__meta
                .gl-card--info__title Primary Button Text Color
                .gl-card--info__description Text color for buttons in forms eg: Below inputs on login page.
              gl-color-picker(:hex="modelConfig.agency.brandBtnTextColor" @hex-updated="(hex) => setConfigAttr({ attr: 'brandBtnTextColor', value: hex })")

              // brandFontWeight
              .gl-card--info__meta
                .gl-card--info__title Primary Button Font Weight
                .gl-card--info__description Font weight for buttons in forms eg: Below inputs on login page.
              b-form-select(:options="fontWeights" v-model="modelConfig.agency.brandFontWeight" @change="(value) => setConfigAttr({ attr: 'brandFontWeight', value: value })")

              // formLabelColor
              .gl-card--info__meta
                .gl-card--info__title Label Color
                .gl-card--info__description Colors for labels above form inputs eg: Above inputs on login page.
              gl-color-picker(:hex="modelConfig.agency.formLabelColor" @hex-updated="(hex) => setConfigAttr({ attr: 'formLabelColor', value: hex })")

              // formLabelFontWeight
              .gl-card--info__meta
                .gl-card--info__title Label Font Weight
                .gl-card--info__description Font weight for labels above form inputs eg: Above inputs on login page.
              b-form-select(:options="fontWeights" v-model="modelConfig.agency.formLabelFontWeight" @change="(value) => setConfigAttr({ attr: 'formLabelFontWeight', value: value })")

              // Header Settings
              .gl-card--info__heading Header Settings

              // headerBgColor
              .gl-card--info__meta
                .gl-card--info__title Background Color
                .gl-card--info__description Header background color.
              gl-color-picker(:hex="modelConfig.agency.headerBgColor" @hex-updated="(hex) => setConfigAttr({ attr: 'headerBgColor', value: hex })")

              // headerTextColor
              .gl-card--info__meta
                .gl-card--info__title Text Color
                .gl-card--info__description Header text color.
              gl-color-picker(:hex="modelConfig.agency.headerTextColor" @hex-updated="(hex) => setConfigAttr({ attr: 'headerTextColor', value: hex })")

              // headerBorderColor
              .gl-card--info__meta
                .gl-card--info__title Border Color
                .gl-card--info__description Border colors in the header.
              gl-color-picker(:hex="modelConfig.agency.headerBorderColor" @hex-updated="(hex) => setConfigAttr({ attr: 'headerBorderColor', value: hex })")

              // headerFontWeight
              .gl-card--info__meta
                .gl-card--info__title Font Weight
                .gl-card--info__description Font weight for the dropdowns in the header.
              b-form-select(:options="fontWeights" v-model="modelConfig.agency.headerFontWeight" @change="(value) => setConfigAttr({ attr: 'headerFontWeight', value: value })")
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

export default {

  // computed
  computed: {
    ...mapGetters(['config'])
  },

  // data
  data () {
    return {
      colors: '',
      fontFamilies: [
        { value: '', text: 'Please select a font family', disabled: true },
        { value: "'ProximaNovaAlt','Helvetica Neue',Helvetica,Arial,sans-serif", text: 'ProximaNovaAlt, Helvetica Neue, Helvetica, Arial, sans-serif' },
        { value: "'Helvetica Neue',Helvetica,Arial,sans-serif", text: 'Helvetica Neue, Helvetica, Arial, sans-serif' }
      ],
      fontWeights: [
        { value: '', text: 'Please select a font weight', disabled: true },
        { value: '400', text: 'Light' },
        { value: '500', text: 'Standard' },
        { value: '600', text: 'Light Bold' },
        { value: '700', text: 'Bold' },
        { value: '800', text: 'Very Bold' }
      ],
      errorList: {
        favicon: [],
        headerLogo: [],
        loginLogo: [],
        emailLogo: []
      },
      successList: {
        favicon: [],
        headerLogo: [],
        loginLogo: [],
        emailLogo: []
      },
      pendingForm: [],
      modelConfig: { agency: {} },
      isLoading: {
        submit: {
          favicon: false,
          headerLogo: false,
          loginLogo: false,
          emailLogo: false
        },
        upsert: false
      }
    }
  },

  // on mount
  mounted () {
    // clear pending list
    this.clearPending()

    // bind modelConfig w/ config copy
    this.bindModelConfig()
  },

  // methods
  methods: {
    cancelChanges () {
      // close all popovers
      this.$root.$emit('bv::hide::popover')

      // clear pending
      this.clearPending()

      // bind modelConfig w/ config copy
      this.bindModelConfig()
    },
    upsertConfig () {
      // close all popovers
      this.$root.$emit('bv::hide::popover')

      // commit config
      this.$glAuth.commitConfig(this.modelConfig)

      // clear pendingForm
      this.clearPending()

      // show success notification
      this.$toasted.show('Settings saved', {
        theme: 'primary',
        type: 'success',
        position: 'top-center',
        duration: 5000
      })

      // upsert config
      this.$glAuth.upsertConfig(this.modelConfig)
        // on error -> show toast
        .catch(e => {
          this.$toasted.show(this.$glForm.parseError(e), {
            theme: 'primary',
            type: 'error',
            position: 'top-center',
            duration: 5000
          })
        })
    },
    bindModelConfig () {
      // bind modelConfig w/ config copy
      this.modelConfig = JSON.parse(JSON.stringify(this.config))
    },
    setConfigAttr (options) {
      // bind model config at the key to base 64
      this.$set(this.modelConfig.agency, options.attr, options.value)

      // update pendingForm
      this.pendingForm.push(options.attr)

      // remove duplicates from pendingForm
      this.pendingForm = _.uniq(this.pendingForm)
    },
    clearPending () {
      // clear pendingForm
      this.pendingForm = []
    }
  }
}
</script>

<style lang="scss" scoped>

</style>



// WEBPACK FOOTER //
// src/views/SettingsAppearance.vue